import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CAPTURE_ROUTES, type UploadSpec } from "@/app/surfaces";
import { useApp } from "./AppContext";
import {
  ApiError,
  DuplicateUploadError,
  UploadCancelled,
  type DuplicateSource,
  type SourceMaterial,
} from "./api";
import { formatBytes, today } from "./format";

/**
 * **One picker, not three routes** — the capture shape both reduced surfaces use.
 *
 * The desktop's full mode has a route per kind, and the route is what tells the Server
 * which kind it is. The phone app (M12) and simple mode (UI-16) both put one drop zone on
 * screen instead, because both are for somebody who has a file and no interest in which of
 * three doors it goes through: the *file* decides, its extension picks the capture spec,
 * and a file matching none of them is refused here by name rather than after four gigabytes
 * have gone up.
 *
 * Shared rather than written twice because the rules are the ones that must not drift: the
 * closed list of extensions is the Server's (CAP-2), the size bound is the Server's, and
 * the refusal names what *is* accepted in the Server's own words for each kind. Two copies
 * would be two chances for a picker to offer a file the Server then refuses.
 *
 * The metadata is what a surface with no pipeline settings can honestly supply: the file's
 * name as the title, today as the date, and "decide for me" for spoken language and
 * expected speakers. Guessing at those would put a wrong answer in front of the diarizer
 * rather than no answer.
 */
export interface AddSource {
  /** Every extension any capture route accepts, which is what the picker offers. */
  accepted: string[];
  /** 0 to 1 while a file is going up, null when nothing is. */
  progress: number | null;
  /** The source that just landed, for the "it is in" line. */
  added: SourceMaterial | null;
  /** CAP-3 — this file is already here, and which one it is. */
  duplicate: DuplicateSource | null;
  problem: string | null;
  /** Take a file: validate it, pick its route, upload it. */
  send: (chosen: File | null) => void;
  /** Abort the transfer in flight. */
  cancel: () => void;
  note: string;
  setNote: (value: string) => void;
  saving: boolean;
  saveNote: () => void;
}

export function useAddSource(): AddSource {
  const { api, refreshRecentSources, refreshRecordings } = useApp();

  const [progress, setProgress] = useState<number | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateSource | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [added, setAdded] = useState<SourceMaterial | null>(null);

  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const transfer = useRef<AbortController | null>(null);

  useEffect(() => () => transfer.current?.abort(), []);

  const accepted = useMemo(() => CAPTURE_ROUTES.flatMap((route) => route.upload?.extensions ?? []), []);

  const send = useCallback(
    (chosen: File | null) => {
      if (!chosen) return;

      setAdded(null);
      setDuplicate(null);
      setProblem(null);

      const dot = chosen.name.lastIndexOf(".");
      const extension = dot >= 0 ? chosen.name.slice(dot).toLowerCase() : "";
      const spec = specFor(extension);

      if (!spec) {
        setProblem(
          extension
            ? `${extension} files are not accepted. ${acceptedSentence()}`
            : `That file has no extension, so there is no way to tell what it is. ${acceptedSentence()}`,
        );
        return;
      }

      if (chosen.size > spec.maxBytes) {
        setProblem(`That file is ${formatBytes(chosen.size)}. ${spec.formatNote}`);
        return;
      }

      const controller = new AbortController();
      transfer.current = controller;
      setProgress(0);

      void api
        .upload(
          {
            kind: spec.kind,
            file: chosen,
            title: chosen.name.replace(/\.[^.]+$/, ""),
            sourceDate: today(),
            // The account's defaults, spelled the way the Server spells "decide for me".
            language: "auto",
            expectedSpeakers: "auto",
          },
          { onProgress: setProgress, signal: controller.signal },
        )
        .then((created) => {
          setAdded(created);
          refreshRecentSources();
          refreshRecordings();
        })
        .catch((cause: unknown) => {
          if (cause instanceof DuplicateUploadError) setDuplicate(cause.duplicate);
          else if (cause instanceof UploadCancelled) setProblem(null);
          else if (cause instanceof ApiError) setProblem(cause.message);
          else setProblem("The upload could not be completed.");
        })
        .finally(() => {
          setProgress(null);
          transfer.current = null;
        });
    },
    [api, refreshRecentSources, refreshRecordings],
  );

  const saveNote = useCallback(() => {
    if (note.trim().length === 0 || saving) return;

    setSaving(true);
    setProblem(null);

    void api
      .createNote({ body: note, sourceDate: today() })
      .then((created) => {
        setAdded(created);
        setNote("");
        refreshRecentSources();
      })
      .catch((cause: unknown) => {
        setProblem(cause instanceof ApiError ? cause.message : "The note could not be saved.");
      })
      .finally(() => setSaving(false));
  }, [api, note, refreshRecentSources, saving]);

  return {
    accepted,
    progress,
    added,
    duplicate,
    problem,
    send,
    cancel: () => transfer.current?.abort(),
    note,
    setNote,
    saving,
    saveNote,
  };
}

/**
 * What to call the thing that just landed, or null when it has no name yet.
 *
 * A note carries neither a title nor a filename — both reduced surfaces send only its text
 * — so the confirmation has to be able to say "it is in" rather than naming a source
 * "Added", which is what a fallback string does when it is used as a name.
 */
export function addedName(source: SourceMaterial): string | null {
  return source.title ?? source.originalFilename ?? null;
}

/** Which capture route accepts this extension — the file picks the route, not the URL. */
function specFor(extension: string): UploadSpec | null {
  return CAPTURE_ROUTES.find((route) => route.upload?.extensions.includes(extension))?.upload ?? null;
}

/** How a refusal names what *is* accepted, in the Server's own words for each kind. */
function acceptedSentence(): string {
  return CAPTURE_ROUTES.map((route) => route.upload?.acceptedSentence)
    .filter(Boolean)
    .join(" ");
}

/**
 * What the pipeline has got a recording to, in a reader's words rather than a status word.
 *
 * The handoff's own wording for the "Recently added" rows on both reduced surfaces. "In
 * your world view" is what `done` means to somebody who never asked what a pipeline is,
 * and it is the whole reason this list is worth showing there at all.
 */
export const PIPELINE_STATE: Record<string, { label: string; dot: string }> = {
  done: { label: "In your world view", dot: "var(--success)" },
  failed: { label: "Could not be read", dot: "var(--destructive)" },
  processing: { label: "Being read now", dot: "var(--vl-bridge)" },
  queued: { label: "Waiting", dot: "var(--warning)" },
  cancelled: { label: "Stopped", dot: "var(--text-muted)" },
};
